import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { TranslatePipe } from '@ngx-translate/core'
import { combineLatest, filter, map } from 'rxjs'
import { ToastService } from '../toast.service'
import { Router } from '@angular/router'
import { UsersService, User } from '../users.service'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { setSeconds } from 'date-fns'
import { MeetingsService, UploadMeetingData } from '../meetings.service'
import { StepProgressComponent } from '../common/step-progress/step-progress.component'
import { DatetimePickerComponent } from '../common/datetime-picker/datetime-picker.component'
import { SelectionComponent } from '../users-selection/selection.component'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { constructNameForUser } from '../utils'

@Component({
  selector: 'app-upload-meeting-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    ToolbarComponent,
    StepProgressComponent,
    DatetimePickerComponent,
    SelectionComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './upload-meeting-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './upload-meeting-page.component.scss',
})
export class UploadMeetingPageComponent implements OnInit {
  private toastService = inject(ToastService)
  private router = inject(Router)
  private usersService = inject(UsersService)
  private meetingsService = inject(MeetingsService)

  currentUserQuery$ = this.usersService.getCurrentUser()
  teamUsers$ = this.usersService.getTeamUsers()

  teamUsersWithoutCurrentUser$ = combineLatest([this.currentUserQuery$, this.teamUsers$]).pipe(
    map(([currentUserResult, teamUsersResult]) => {
      if (currentUserResult.isSuccess && teamUsersResult.isSuccess) {
        const currentUser = currentUserResult.data
        const filteredUsers = teamUsersResult.data!.filter((user) => user.id !== currentUser!.id)
        return { isSuccess: true, data: filteredUsers }
      } else {
        return { isSuccess: false, data: [] }
      }
    })
  )

  stepLabels = [
    'MEETING_SETUP.STEPS.MEETING_DETAILS',
    'MEETING_SETUP.STEPS.TEMPLATE_SOURCE',
    'MEETING_SETUP.STEPS.REVIEW',
    'MEETING_SETUP.STEPS.UPLOAD_RECORDING',
  ]

  data: UploadMeetingData = {
    name: '',
    date: setSeconds(new Date(), 0),
    userIds: [],
  }

  uploadMeetingMutation = this.meetingsService.uploadMeeting()

  get isCreateButtonDisabled(): boolean {
    return this.data.name.trim() === ''
  }

  ngOnInit() {
    this.uploadMeetingMutation.result$
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((meeting) => {
        this.toastService.show('UPLOAD_MEETING.IMPORT_SUCCESSFUL_NOTIFICATION')
        this.router.navigate(['/app', 'meetings', meeting!.id])
      })
  }

  addFullNameToUserList(users: User[]) {
    return users.map((user) => {
      return {
        ...user,
        fullName: constructNameForUser(user),
      }
    })
  }

  onUserSelectionChange(users: User[]) {
    this.data.userIds = users.map((user) => user.id)
  }

  onCreateMeetingClicked() {
    this.uploadMeetingMutation.mutate(this.data)
  }
}
